import { createSlice } from "@reduxjs/toolkit";

/* items are cart lines (one per product + denomination, keyed by lineId) mapped
   from CartDto. Product details (discount, fees, stock, limits) are kept
   separately, keyed by SKU, with the time of the last successful fetch so the
   saga can reuse them within the cache window. */
// updatingLineId while several lines are being removed at once.
export const REMOVING_LINES = "removing-lines";

export const cartSlice = createSlice({
  name: "cart",
  initialState: {
    isLoading: false,
    isError: false,
    message: "",
    items: [],
    cartLimitAmount: null,
    productDetailsBySku: {},
    productDetailsFetchedAt: null,
    // SKUs sent in the last successful details call, including any the API did not return.
    productDetailsSkus: [],
    updatingLineId: null,
    updateFailed: false,
    updateError: "",
  },
  reducers: {
    onFetchCart: (state) => ({
      ...state,
      isLoading: true,
      isError: false,
      message: "",
    }),

    onFetchCartSuccess: (state, { payload }) => ({
      ...state,
      items: payload?.items || [],
      cartLimitAmount: payload?.cartLimitAmount ?? null,
    }),

    onCartProductDetailsSuccess: (state, { payload }) => {
      const productDetailsBySku = {};
      (payload?.data || []).forEach((product) => {
        productDetailsBySku[product.sku] = product;
      });
      return {
        ...state,
        isLoading: false,
        productDetailsBySku,
        productDetailsSkus: payload?.skuIds || [],
        productDetailsFetchedAt: payload?.fetchedAt,
      };
    },

    /* Header count refresh: GET cart only, no product details. Lines replace the
       stored ones; a failure keeps the last known lines. */
    onFetchCartCount: (state) => state,

    onFetchCartCountSuccess: (state, { payload }) => ({
      ...state,
      items: payload?.items || [],
    }),

    onFetchCartComplete: (state) => ({
      ...state,
      isLoading: false,
    }),

    onFetchCartError: (state, { payload }) => ({
      ...state,
      isLoading: false,
      isError: true,
      message: payload?.message || "",
    }),

    /* payload: { memberId, line, qty } - qty is the line's new quantity. */
    onAddToCart: (state, { payload }) => ({
      ...state,
      updatingLineId: payload?.line?.lineId ?? null,
      updateFailed: false,
      updateError: "",
    }),

    /* payload: { memberId, lines } - sets all these lines to quantity 0 in one
       call (used before checkout to drop out of stock / unavailable lines). */
    onRemoveCartLines: (state) => ({
      ...state,
      updatingLineId: REMOVING_LINES,
      updateFailed: false,
      updateError: "",
    }),

    onAddToCartSuccess: (state, { payload }) => ({
      ...state,
      items: payload?.items || state.items,
      updatingLineId: null,
    }),

    onAddToCartError: (state, { payload }) => ({
      ...state,
      updatingLineId: null,
      updateFailed: true,
      // API message when there is one; the page falls back to a translated text.
      updateError: payload?.message || "",
    }),

    onClearCartUpdateError: (state) => ({
      ...state,
      updateFailed: false,
      updateError: "",
    }),
  },
});

export const {
  onFetchCart,
  onFetchCartSuccess,
  onCartProductDetailsSuccess,
  onFetchCartCount,
  onFetchCartCountSuccess,
  onFetchCartComplete,
  onFetchCartError,
  onAddToCart,
  onRemoveCartLines,
  onAddToCartSuccess,
  onAddToCartError,
  onClearCartUpdateError,
} = cartSlice.actions;

export default cartSlice.reducer;
